import DutyStatusLog, { IDutyStatusLog } from "../model/DutyStatusLog";
import Api from "../services/api";
import { DriveStore } from "../store/reducers";
import { getServerDate } from "../utils/storage/date";
import * as logger from "../utils/logger";
import { dstCalculator, getDateTokenString, getPreviousDayStart, toDateTime } from "../utils/date";
import MultiCall from "../utils/multiCall";
import { IDailyOdometer, IDayOdometer } from "../model/DutyStatusLogs";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { extend } from "../utils/vanilla";
import { DailyOdometerActions } from "../store/dailyOdometer/actions";
import { compareDatesWithSequenceIds, isStatusLog, doesStatusNeedDistance, isDistanceProbable, isDrivingStatus } from "hos-tools";
import { SECONDS } from "../constants";
import { IApiError } from "../utils/api.interfaces";
import { ISuspiciousLogsSent } from "../store/suspiciousLogsSent/types";
import { SuspiciousLogsSentActions } from "../store/suspiciousLogsSent/actions";
import { getLogKnownDeviceId, removeHiddenRequestedAdds } from "./hosSyncHelpers";

interface IOdometerLookupData {
    lookupDictionary: IIdDictionary<Set<string>>;
    skippedLookupDictionary: IIdDictionary<Set<string>>;
    count: number;
}

interface ITempDistanceCache {
    suspiciousDistanceString: string;
    originalDistance: number;
}

interface IRecalculateLogData {
    log: DutyStatusLog;
    nextLogDateTime: string;
}

const NO_USER_ID = "NoUserId";
export const THIRTY_SECONDS = 30 * SECONDS;
const MAX_ODOMETER_LOOKUP_COUNT = 50;
const LOG_RECORDS_LIMIT = 500; // Limit logRecords results to 500 to prevent overhead

const isLatestOdometer = (log: DutyStatusLog, currentServerTimeString: string) => log.dateTime === currentServerTimeString;

const isMidnight = (localTime: Date) => localTime.getHours() === 0 && localTime.getMinutes() === 0 && localTime.getSeconds() === 0 && localTime.getMilliseconds() === 0;

const isServerTimeWithinRetentionPeriod = (retentionStartTime: Date, currentServerTime: Date) => currentServerTime.getTime() - retentionStartTime.getTime() > THIRTY_SECONDS;

const isOutsideOfRetentionPeriod = (retentionStartTime: Date, log: DutyStatusLog) => new Date(log.dateTime).getTime() < retentionStartTime.getTime();

const isDeviceChanged = (deviceId: string, nextDeviceId: string | null) => deviceId !== nextDeviceId;

const isEmergencyResponseExemptionLog = (log: IDutyStatusLog) =>
    log.status === "EmergencyResponseExemptionEnabled" || log.status === "EmergencyResponseExemptionDisabled";

export class OdometerData {
    private latestOdometerDateTime  = new Date(0);
    private multiCall: MultiCall;
    private myGeotabVersion: string;

    constructor (
        private readonly api: Api,
        private readonly store: DriveStore
    ) {}

    fetchAndPopulateOdometerData(retentionStartTime: Date, localLogs: IIdDictionary<DutyStatusLog>) {
        return new Promise<IIdDictionary<DutyStatusLog>>(resolve => {
            this.myGeotabVersion = this.store.getState().data.clientVersion;
            const currentServerTime = getServerDate();
            const odometerCache: IIdDictionary<IIdDictionary<number>> = {};

            let merged = this.findLogWithoutOdometer(localLogs, odometerCache, currentServerTime, retentionStartTime);
            this.multiCall.beginInvoke()
                .then(() => {
                    const currentServerTimeString = currentServerTime.toISOString();
                    this.populateOdometer(merged, odometerCache, currentServerTimeString);

                    return this.processDistanceNew(merged, currentServerTimeString, odometerCache);
                })
                .then(() => resolve(localLogs))
                .catch((error: IApiError) => {
                    let requestIndex = error?.data?.requestIndex;
                    let call = requestIndex !== undefined ? this.multiCall.getFailedCall(requestIndex) : null;
                    const deviceId = call?.params?.deviceSearch?.id ?? "Unknown device";
                    const dateTimes = call?.params?.dateTimes ?? [];
                    logger.error(
                        logger.Source.SYNC,
                        `CalculateHosOdometer device id: ${ deviceId } datetime: ${ dateTimes.join(", ") } error: ${ error?.message || "Unknown" }`,
                        this.api.getUser()?.id,
                        error
                    );
                    resolve(localLogs);
                });
        });
    }

    /**
     * Loads and computes odometer data for a single historical day's logs (outside the sync window).
     * Mirrors what the in-window sync does — looks up any missing odometer values via
     * CalculateHosOdometer, sets `log.distance` on each log ({@link processDistanceNew}), and returns
     * the per-day/per-device start/end bounds — but returns the bounds to the caller (the historical
     * loader caches them) rather than dispatching to the shared `dailyOdometer` store.
     *
     * Unlike {@link findLogWithoutOdometer} there is no retention-window break and no "latest
     * odometer" lookup: historical days are entirely in the past, so a real server time is passed as
     * the current-server-time (no historical log can equal it, keeping the "don't finalize the latest
     * distance" branches correctly inert).
     *
     * @param logs - The day's logs (user + system + continuation placeholder), sorted ascending.
     */
    fetchOdometerForHistoricalLogs(logs: DutyStatusLog[]): Promise<IDailyOdometer> {
        return new Promise<IDailyOdometer>((resolve, reject) => {
            this.myGeotabVersion = this.store.getState().data.clientVersion;
            const currentServerTimeString = getServerDate().toISOString();
            const odometerCache: IIdDictionary<IIdDictionary<number>> = {};
            const odometerLookupData = this.createOdometerLookupData();

            const visibleLogs = removeHiddenRequestedAdds(logs);
            this.multiCall = new MultiCall(this.api);
            for (const log of visibleLogs) {
                if (isEmergencyResponseExemptionLog(log)) {
                    continue;
                }

                const deviceId = getLogKnownDeviceId(log);
                if (!deviceId) {
                    continue;
                }
                if (!log.odometer) {
                    // API users cannot set Odometer on a DutyStatusLog edit, so an accepted carrier
                    // edit comes back with its reading reset to 0. Treat a missing/0 reading the same
                    // way — normalize to undefined and resolve it from raw-odometer StatusData via
                    // CalculateHosOdometer (resolveOdometerFromCache only fills logs whose odometer is
                    // undefined, and computeDailyOdometer treats 0 as "unset" and lets a later log
                    // overwrite the day's start).
                    log.odometer = undefined;
                    this.addCalls(odometerLookupData, odometerCache, deviceId, log.dateTime);
                } else {
                    this.addOdometer(odometerCache, deviceId, log.dateTime, log.odometer);
                }
            }
            this.calculateAndLogSkippedEntries(odometerLookupData);
            this.createMulticallForOdometerLookup(odometerLookupData, odometerCache);

            this.multiCall.beginInvoke()
                .then(() => {
                    // computeDailyOdometer must run before processDistanceNew: it resolves looked-up
                    // odometer values onto the logs (via shouldProcessLog -> resolveOdometerFromCache),
                    // which processDistanceNew then reads to compute distances. This mirrors the
                    // in-window order (populateOdometer before processDistanceNew).
                    const dailyOdometer = this.computeDailyOdometer(visibleLogs, odometerCache, currentServerTimeString);
                    return this.processDistanceNew(visibleLogs, currentServerTimeString, odometerCache).then(() => dailyOdometer);
                })
                .then(dailyOdometer => resolve(dailyOdometer))
                .catch((error: IApiError) => {
                    logger.error(
                        logger.Source.SYNC,
                        `CalculateHosOdometer (historical) error: ${ error?.message || "Unknown" }`,
                        this.api.getUser()?.id,
                        error
                    );
                    reject(error);
                });
        });
    }

    private addOdometer(odometerCache: IIdDictionary<IIdDictionary<number>>, deviceId: string, dateTime: string, value: number) {
        odometerCache[deviceId] = odometerCache[deviceId] || {};
        odometerCache[deviceId][dateTime] = value;
    }

    private createMultiCall(odometerCache: IIdDictionary<IIdDictionary<number>>, deviceId: string, dateTimes: string[]) {
        if (deviceId) {
            this.multiCall.add(
                "CalculateHosOdometer",
                {
                    deviceSearch: { id: deviceId },
                    dateTimes
                },
                (results: IOdometerData[]) => {
                    if (results) {
                        results.forEach(result => {
                            this.addOdometer(odometerCache, deviceId, result.dateTime, result.valueInMeters);
                        });
                    } else {
                        logger.error(logger.Source.SYNC, `Missing results for CalculateHosOdometer device id: ${ deviceId } datetime: ${ dateTimes.join(", ") }`);
                    }
                }
            );
        }
    }

    private addToLookupDictionary(odometerLookupData: IOdometerLookupData, deviceId: string, dateTime: string, forceAdd = false) {
        const limitReached = odometerLookupData.count >= MAX_ODOMETER_LOOKUP_COUNT && !forceAdd;
        const dictionary = limitReached ? odometerLookupData.skippedLookupDictionary : odometerLookupData.lookupDictionary;
        if (!dictionary[deviceId]) {
            dictionary[deviceId] = new Set();
        }
        const alreadyExists = dictionary[deviceId].has(dateTime);
        if (!alreadyExists) {
            dictionary[deviceId].add(dateTime);
            !limitReached && odometerLookupData.count++;
        }
        return !limitReached;
    }

    private addCalls(odometerLookupData: IOdometerLookupData, odometerCache: IIdDictionary<IIdDictionary<number>>, requestedDeviceId: string | null, dateTime: string) {
        let deviceId = requestedDeviceId;
        if (!deviceId || (odometerCache[deviceId] && odometerCache[deviceId][dateTime])) {
            return;
        }

        this.addToLookupDictionary(odometerLookupData, deviceId, dateTime);
    }

    private createOdometerLookupData(): IOdometerLookupData {
        return {
            lookupDictionary: {},
            skippedLookupDictionary: {},
            count: 0
        };
    }

    private readonly isStandardLog = (log: IDutyStatusLog) => log.driver && log.driver.id && log.driver.id !== NO_USER_ID && isStatusLog(log);

    private getLatestLog(logs: IDutyStatusLog[]) {
        // logs are sorted in descending order
        for (let i = 0; i < logs.length; i++) {
            if (this.isStandardLog(logs[i])) {
                return logs[i];
            }
        }
        return null;
    }

    private findLogWithoutOdometer(localLogs: IIdDictionary<DutyStatusLog>, odometerCache: IIdDictionary<IIdDictionary<number>>, currentServerTime: Date, retentionStartTime: Date) {
        const currentServerTimeString = currentServerTime.toISOString();
        const odometerLookupData = this.createOdometerLookupData();
        const logsToProcess: DutyStatusLog[] = [];

        // Sorted from newest -> oldest
        let logs = Object.keys(localLogs).reduce((result, logId) => {
            result.push(localLogs[logId]);
            return result;
        }, [] as DutyStatusLog[]).sort((a, b) => compareDatesWithSequenceIds(b, a));

        // do not include hidden logs in the odometer lookup or distance calculation
        logs = removeHiddenRequestedAdds(logs);
        logs.sort((a, b) => compareDatesWithSequenceIds(b, a));

        let nextDeviceId: string | null = null;
        let nextLog: DutyStatusLog | null = null;
        let currentDeviceId = logs.length ? getLogKnownDeviceId(logs[0]) : null;

        // Get current odometer records (needed for distance & compliance print)
        if (currentDeviceId
            && isServerTimeWithinRetentionPeriod(retentionStartTime, currentServerTime)
            && currentServerTime.getTime() - this.latestOdometerDateTime.getTime() > THIRTY_SECONDS) {
                const addCallForLatestOdometer = () => {
                    nextLog = { device: { id: currentDeviceId }, dateTime: currentServerTimeString } as DutyStatusLog;
                    nextDeviceId = currentDeviceId;
                    logsToProcess.push(nextLog);
                    this.addCalls(odometerLookupData, odometerCache, currentDeviceId, currentServerTimeString);
                    this.latestOdometerDateTime = currentServerTime;
                };

                const latestLog = this.getLatestLog(logs);
                if (!latestLog || isDrivingStatus(latestLog)) {
                    addCallForLatestOdometer();
                }
        }
        this.multiCall = new MultiCall(this.api);

        for (let i = 0; i < logs.length; i++) {
            let log = logs[i];
            if (isEmergencyResponseExemptionLog(log)) {
                continue;
            }

            currentDeviceId = getLogKnownDeviceId(log);

            if (isOutsideOfRetentionPeriod(retentionStartTime, log)) {
                break;
            }

            if (!currentDeviceId) {
                if (this.isStandardLog(log)) {
                    nextDeviceId = currentDeviceId;
                    nextLog = log;
                }
                continue;
            }

            if (log.odometer === undefined) {
                this.addCalls(odometerLookupData, odometerCache, currentDeviceId, log.dateTime);
            } else {
                this.addOdometer(odometerCache, currentDeviceId, log.dateTime, log.odometer);
            }

            if (this.isStandardLog(log)) {
                if (isDeviceChanged(currentDeviceId, nextDeviceId) && nextLog && doesStatusNeedDistance(log) && this.shouldLookupOdometerForChangedDevice(log, nextLog, currentServerTimeString)) {
                    // Edge case, if the device changed, from the previouslog to currentlog
                    // and the currentlog is exactly at midnight, then we want to fetch the
                    // odometer for the previous device right 1 millisecond before midnight
                    let dateTime = isMidnight(new Date(dstCalculator.toLocalDateTime(nextLog.dateTime, this.api.getUser()?.timeZoneId)))
                                    ? new Date(new Date(nextLog.dateTime).getTime() - 1).toISOString()
                                    : nextLog.dateTime;
                    logsToProcess.push({ device: { id: currentDeviceId }, dateTime } as DutyStatusLog);
                    this.addCalls(odometerLookupData, odometerCache, currentDeviceId, dateTime);
                }
                nextDeviceId = currentDeviceId;
                nextLog = log;
            }

            logsToProcess.push(log);
        }

        this.calculateAndLogSkippedEntries(odometerLookupData);
        this.createMulticallForOdometerLookup(odometerLookupData, odometerCache);

        return logsToProcess.reverse();
    }

    private calculateAndLogSkippedEntries(odometerLookupData: IOdometerLookupData, recalculatedCount?: number) {
        const skippedDeviceIds = Object.keys(odometerLookupData.skippedLookupDictionary);
        if (skippedDeviceIds.length > 0) {
            const totalCounts: string[] = [];
            skippedDeviceIds.forEach(deviceId => totalCounts.push(`${ deviceId } (${ odometerLookupData.skippedLookupDictionary[deviceId].size })`));
            logger.error(
                logger.Source.SYNC,
                `CalculateHosOdometer lookup count exceeded.${ (recalculatedCount ? ` Count of logs that require recalculated distance: ${ recalculatedCount }. ` : " ") }Counts per device: ${ totalCounts.join(", ") }`
            );
        } else if (recalculatedCount) {
            logger.error(
                logger.Source.SYNC,
                `CalculateHosOdometer count of logs that require recalculated distance: ${ recalculatedCount }.`
            );
        }
    }

    private createMulticallForOdometerLookup(odometerLookupData: IOdometerLookupData, odometerCache: IIdDictionary<IIdDictionary<number>>) {
        Object.keys(odometerLookupData.lookupDictionary).forEach(deviceId => this.createMultiCall(odometerCache, deviceId, Array.from(odometerLookupData.lookupDictionary[deviceId])));
    }

    private resolveOdometerFromCache(log: DutyStatusLog, deviceId: string, odometerCache: IIdDictionary<IIdDictionary<number>>, currentServerTime: string): boolean {
        if (log.odometer === undefined) {
            if (deviceId && odometerCache[deviceId] && odometerCache[deviceId].hasOwnProperty(log.dateTime)) {
                log.odometer = odometerCache[deviceId][log.dateTime];
            } else {
                // to be addressed in future fix
                if (log.dateTime === currentServerTime) {
                    return false;
                }
                return false;
            }
        }
        return true;
    }

    private initializeDayOdometer(dailyOdometer: IDailyOdometer, logDate: string, deviceId: string, logOdometerValue: number): void {
        if (!dailyOdometer[logDate]) {
            dailyOdometer[logDate] = {};
        }
        if (!dailyOdometer[logDate][deviceId]) {
            dailyOdometer[logDate][deviceId] = {
                start: logOdometerValue,
                end: logOdometerValue
            } as IDayOdometer;
        }
    }

    private updateDayOdometerBounds(
        dailyOdometer: IDailyOdometer,
        logDate: string,
        deviceId: string,
        logOdometerValue: number
    ): void {
        const cur = dailyOdometer[logDate][deviceId];
        if (cur.start === 0 || logOdometerValue < cur.start) {
            cur.start = logOdometerValue;
        }
        if (cur.end < logOdometerValue) {
            cur.end = logOdometerValue;
        }
    }

    private tryUpdatePreviousDayEnd(dailyOdometer: IDailyOdometer, log: DutyStatusLog, deviceId: string, logOdometerValue: number, apiUser: IUser | null): void {
        const localTime = new Date(dstCalculator.toLocalDateTime(log.dateTime, apiUser?.timeZoneId));

        if (log.placeholder || isMidnight(localTime)) {
            const prev = getDateTokenString(dstCalculator.toLocalDateTime(getPreviousDayStart(new Date(log.dateTime)).toISOString()));
            const prevDayOdometer = dailyOdometer[prev]?.[deviceId];
            if (prevDayOdometer && prevDayOdometer.end < logOdometerValue) {
                prevDayOdometer.end = logOdometerValue;
            }
        }
    }

    private shouldProcessLog(log: DutyStatusLog, deviceId: string | null, odometerCache: IIdDictionary<IIdDictionary<number>>, currentServerTime: string): boolean {
        if (isEmergencyResponseExemptionLog(log) || !deviceId || !odometerCache[deviceId]) {
            return false;
        }

        const isResolved = this.resolveOdometerFromCache(log, deviceId, odometerCache, currentServerTime);

        if (!log.status || !(log.driver && log.driver.id && log.driver.id !== NO_USER_ID)) {
            return false;
        }
        return isResolved;
    }

    private updateDailyLogCounts(
        dailyLogCounts: IIdDictionary<IIdDictionary<{ total: number; placeholders: number; hasDrivingPlaceholder: boolean }>>,
        logDate: string,
        deviceId: string,
        log: DutyStatusLog
    ): void {
        if (!dailyLogCounts[logDate]) {
            dailyLogCounts[logDate] = {};
        }
        if (!dailyLogCounts[logDate][deviceId]) {
            dailyLogCounts[logDate][deviceId] = { total: 0, placeholders: 0, hasDrivingPlaceholder: false };
        }
        dailyLogCounts[logDate][deviceId].total++;
        if (log.placeholder) {
            dailyLogCounts[logDate][deviceId].placeholders++;
            if (isDrivingStatus(log)) {
                dailyLogCounts[logDate][deviceId].hasDrivingPlaceholder = true;
            }
        }
    }

    private cleanupHiddenDays(dailyOdometer: IDailyOdometer, dailyLogCounts: IIdDictionary<IIdDictionary<{ total: number; placeholders: number; hasDrivingPlaceholder: boolean }>>) {
        Object.keys(dailyLogCounts).forEach(date => {
            const deviceCounts = dailyLogCounts[date];
            Object.keys(deviceCounts).forEach(deviceId => {
                const counts = deviceCounts[deviceId];
                if (counts && counts.total === counts.placeholders && !counts.hasDrivingPlaceholder && dailyOdometer[date] && dailyOdometer[date][deviceId]) {
                    delete dailyOdometer[date][deviceId];
                }
            });

            if (dailyOdometer[date] && Object.keys(dailyOdometer[date]).length === 0) {
                delete dailyOdometer[date];
            }
        });
    }

    private populateOdometer(logs: DutyStatusLog[], odometerCache: IIdDictionary<IIdDictionary<number>>, currentServerTime: string) {
        let apiUserId = this.api.getUser()?.id;
        if (!apiUserId) {
            return;
        }
        const dailyOdometer = this.computeDailyOdometer(logs, odometerCache, currentServerTime);
        this.store.dispatch(DailyOdometerActions.setDailyOdometer({ userId: apiUserId, dailyOdometer: dailyOdometer }));
    }

    /**
     * Computes the per-day, per-device odometer start/end bounds for the given logs without touching
     * the store. `populateOdometer` (in-window sync) dispatches the result to the shared
     * `dailyOdometer` store; the historical-day path caches it in the historicalDayTracker entry
     * instead (the shared store is replaced wholesale on every sync and would wipe historical days).
     *
     * Logs must be sorted ascending (oldest -> newest); the day/placeholder bookkeeping relies on
     * forward iteration.
     */
    private computeDailyOdometer(logs: DutyStatusLog[], odometerCache: IIdDictionary<IIdDictionary<number>>, currentServerTime: string): IDailyOdometer {
        let apiUser = this.api.getUser();
        let dailyOdometer: IDailyOdometer = {};
        let dailyLogCounts: IIdDictionary<IIdDictionary<{ total: number; placeholders: number; hasDrivingPlaceholder: boolean }>> = {};
        let isLastLogNonDrivingPlaceholder = false;
        let lastDateString: string | undefined = undefined;
        for (let i = 0; i < logs.length; i++) {
            let log = logs[i];
            let deviceId = getLogKnownDeviceId(log);

            if (!deviceId || !this.shouldProcessLog(log, deviceId, odometerCache, currentServerTime)) {
                continue;
            }

            // get the date this odo belongs to
            let logDate = getDateTokenString(dstCalculator.toLocalDateTime(log.dateTime));

            this.updateDailyLogCounts(dailyLogCounts, logDate, deviceId, log);

            const logOdometerValue = log.odometer ?? 0;

            // init dictionary
            this.initializeDayOdometer(dailyOdometer, logDate, deviceId, logOdometerValue);
            this.updateDayOdometerBounds(dailyOdometer, logDate, deviceId, logOdometerValue);

            // last log was placeholder, replace it as the starting odometer. this is the first log of the day
            if (isLastLogNonDrivingPlaceholder && lastDateString === logDate) {
                if (dailyOdometer[logDate] && dailyOdometer[logDate][deviceId]) {
                    dailyOdometer[logDate][deviceId].start = logOdometerValue;
                }
            }
            if (lastDateString !== undefined && lastDateString !== logDate && doesStatusNeedDistance(log)) {
                // Date changed, update previous day's end odo if needed
                this.tryUpdatePreviousDayEnd(dailyOdometer, log, deviceId, logOdometerValue, apiUser);
            }
            isLastLogNonDrivingPlaceholder = log.placeholder && !doesStatusNeedDistance(log) || false;
            lastDateString = logDate;
        }

        this.cleanupHiddenDays(dailyOdometer, dailyLogCounts);

        return dailyOdometer;
    }

    private shouldLookupOdometerForChangedDevice(log: DutyStatusLog, nextLog: DutyStatusLog, currentServerTimeString: string) {
        const isLogWithSameDateTimeAndZeroDistance = log.dateTime === nextLog.dateTime && log.distance === 0;
        const isNextLogDateTimeChanged = log._nextLogDateTime && log._nextLogDateTime !== nextLog.dateTime;
        const isMissingDistanceOrDistanceImprobable = log.distance === undefined || log.distance <= 0 || !isDistanceProbable(log.distance, toDateTime(log.dateTime), toDateTime(nextLog.dateTime));
        return isLatestOdometer(nextLog, currentServerTimeString)
            || (!isLogWithSameDateTimeAndZeroDistance && (isMissingDistanceOrDistanceImprobable || isNextLogDateTimeChanged));
    }

    private getSuspiciousDistanceLogString(distance: number, currentLog: DutyStatusLog, nextLog: DutyStatusLog) {
        const header = `Distance:${ distance } meters.`;
        const getLogString = (logData: DutyStatusLog) => {
            const logId = logData.id ? `id='${ logData.id }',` : "";
            const logStatus = logData.status ? `status='${ logData.status }',` : "";
            const logDevice = `device='${ logData.device.id }',`;
            const logOdometer = `odometer='${ logData.odometer || 0 }'`;
            return `${ logId }${ logData.dateTime },${ logStatus }${ logDevice }${ logOdometer }`;
        };

        const currentLogString = getLogString(currentLog);
        const nextLogString = getLogString(nextLog);

        return `${ header }\r\nCurrentLog:${ currentLogString }\r\nNextLog:${ nextLogString }`;
    }

    private processDistanceNew(mergedLogs: DutyStatusLog[], currentServerTimeString: string, odometerCache: IIdDictionary<IIdDictionary<number>>) {
        const [logsToRecalculate, logWithImprobableDistanceCache] = this.populateDistanceNew(mergedLogs, currentServerTimeString, odometerCache);
        return this.populateDistancesByRecalculatedOdometerNew(logsToRecalculate, odometerCache, logWithImprobableDistanceCache)
            .then(logsToRecalculateWithGps => this.populateDistancesByLogRecordsNew(logsToRecalculateWithGps, logWithImprobableDistanceCache))
            .then(() => this.logSuspiciousDistancesThatWereNotRecalculated(logWithImprobableDistanceCache));
    }

    private setDistanceAndOriginalDistanceOnLog(log: DutyStatusLog, distance: number, originalDistance?: number) {
        log.distance = distance;
        if (originalDistance !== undefined) {
            log._originalDistance = originalDistance;
        }
    }

    private populateDistanceNew(
        logs: DutyStatusLog[],
        currentServerTimeString: string,
        odometerCache: IIdDictionary<IIdDictionary<number>>
    ): [IRecalculateLogData[], IIdDictionary<ITempDistanceCache>] {
        const logsToRecalculate: IRecalculateLogData[] = [];
        const logWithImprobableDistanceCache: IIdDictionary<ITempDistanceCache> = {};
        const standardLogs: DutyStatusLog[] = logs.filter(log => this.isStandardLog(log) || !log.status);

        for (let i = 0; i < standardLogs.length - 1; i++) {
            let log = standardLogs[i];
            let nextLog = standardLogs[i + 1];
            const currentDeviceId = getLogKnownDeviceId(log);
            if (log && nextLog && currentDeviceId && doesStatusNeedDistance(log)) {
                if (!nextLog.status) {
                    // the pseudo log should be based on the log right after it
                    const realNextLog = standardLogs[i + 2];
                    log._nextLogDateTime = realNextLog ? realNextLog.dateTime : undefined;
                }

                const isDifferentDevice = currentDeviceId && isDeviceChanged(currentDeviceId, getLogKnownDeviceId(nextLog));
                if (isDifferentDevice) {
                    // cannot process this
                    continue;
                }

                // Previously was a different device next, but now same device, so delete nextLogDateTime
                if (!isDifferentDevice && nextLog.status && log._nextLogDateTime !== undefined) {
                    delete log._nextLogDateTime;
                }

                const distance = Math.floor(nextLog.odometer || 0) - Math.floor(log.odometer || 0);
                if (log.distance !== undefined && log._originalDistance !== undefined && distance === log._originalDistance) {
                    continue;
                }

                const isLogDistanceProbable = isDistanceProbable(distance, toDateTime(log.dateTime), toDateTime(nextLog.dateTime));
                const isNextLogLatestOdometer = isLatestOdometer(nextLog, currentServerTimeString);
                // If nextLog is the latest datetime, don't bother logging improbable distance or recalculating, it will be updated later
                if (!isLogDistanceProbable && !isNextLogLatestOdometer) {
                    logWithImprobableDistanceCache[log.id] = {
                        suspiciousDistanceString: this.getSuspiciousDistanceLogString(distance, log, nextLog),
                        originalDistance: distance
                    };

                    logsToRecalculate.push({ log: log, nextLogDateTime: nextLog.dateTime });

                    // Delete this because we are going to look it up again
                    if (odometerCache[currentDeviceId]) {
                        delete odometerCache[currentDeviceId][log.dateTime];
                        delete odometerCache[currentDeviceId][nextLog.dateTime];
                    }
                } else {
                    // Don't want to set originalDistance yet if it's using the latest status data, don't 'finalize' distance until the next log comes in
                    this.setDistanceAndOriginalDistanceOnLog(log, Math.max(0, distance), isNextLogLatestOdometer ? undefined : distance);
                }
            }
        }

        return [logsToRecalculate.reverse(), logWithImprobableDistanceCache];
    }

    private populateDistancesByRecalculatedOdometerNew(
        logsToRecalculate: IRecalculateLogData[],
        odometerCache: IIdDictionary<IIdDictionary<number>>,
        logWithImprobableDistanceCache: IIdDictionary<ITempDistanceCache>
    ) {
        return new Promise<IRecalculateLogData[]>((resolve, reject) => {
            if (logsToRecalculate.length > 0) {
                const getFromCache = (dateTime: string, deviceId: string | null) => {
                    if (deviceId && odometerCache[deviceId] && odometerCache[deviceId][dateTime] !== undefined) {
                        return odometerCache[deviceId][dateTime];
                    }
                    return undefined;
                };
                const odometerLookupData = this.createOdometerLookupData();
                const logsToRecalculateWithGps: IRecalculateLogData[] = [];

                this.multiCall = new MultiCall(this.api);
                logsToRecalculate.forEach(({ log, nextLogDateTime }) => {
                    const deviceId = getLogKnownDeviceId(log);
                    if (deviceId) {
                        const added = this.addToLookupDictionary(odometerLookupData, deviceId, log.dateTime);
                        this.addToLookupDictionary(odometerLookupData, deviceId, nextLogDateTime, added);
                        if (!added) {
                            // Don't log this yet, we aren't going to be able to recalculate it this time due to the limit
                            delete logWithImprobableDistanceCache[log.id];
                        }
                    }
                });

                this.calculateAndLogSkippedEntries(odometerLookupData, logsToRecalculate.length);
                this.createMulticallForOdometerLookup(odometerLookupData, odometerCache);

                this.multiCall.beginInvoke().then(() => {
                    logsToRecalculate.forEach(({ log, nextLogDateTime }) => {
                        const deviceId = getLogKnownDeviceId(log);
                        const logOdometer = getFromCache(log.dateTime, deviceId);
                        const nextLogOdometer = getFromCache(nextLogDateTime, deviceId);
                        // If either odometer in cache is undefined, means we were not able get the odometer (ie. due to the limit), so leave distance uncalculated until next sync
                        if (logOdometer !== undefined && nextLogOdometer !== undefined) {
                            const distance = Math.floor(nextLogOdometer) - Math.floor(logOdometer);
                            if (isDistanceProbable(distance, toDateTime(log.dateTime), toDateTime(nextLogDateTime))) {
                                this.setDistanceAndOriginalDistanceOnLog(log, distance, logWithImprobableDistanceCache[log.id].originalDistance);
                                delete logWithImprobableDistanceCache[log.id];
                            } else {
                                logsToRecalculateWithGps.push({ log, nextLogDateTime });
                            }
                        }
                    });
                    resolve(logsToRecalculateWithGps);
                }).catch(reject);
            } else {
                resolve([]);
            }
        });
    }

    private populateDistancesByLogRecordsNew(logsToRecalculate: IRecalculateLogData[], logWithImprobableDistanceCache: IIdDictionary<ITempDistanceCache>) {
        return new Promise((resolve, reject) => {
            if (logsToRecalculate.length > 0) {
                const setDistanceToOriginalValue = (log: DutyStatusLog) => {
                    const originalDistance = logWithImprobableDistanceCache[log.id].originalDistance;
                    this.setDistanceAndOriginalDistanceOnLog(log, Math.max(0, originalDistance), originalDistance);
                };
                const getLogRecordsForDistance = (logToRecalculate: DutyStatusLog, logEnd: string) => {
                    const deviceId = getLogKnownDeviceId(logToRecalculate);
                    this.multiCall.add("Get", {
                        typeName: "LogRecord",
                        search: {
                            deviceSearch: { id: deviceId },
                            fromDate: logToRecalculate.dateTime,
                            toDate: logEnd
                        },
                        resultsLimit: LOG_RECORDS_LIMIT
                    });
                };
                this.multiCall = new MultiCall(this.api);

                logsToRecalculate.forEach(({ log, nextLogDateTime }) => getLogRecordsForDistance(log, nextLogDateTime));
                this.multiCall.beginInvoke()
                    .then(logsSpeedData => {
                        logsToRecalculate.forEach(({ log, nextLogDateTime }, index) => {
                            const speedData = logsSpeedData[index];
                            if (speedData && speedData.length && speedData.length < LOG_RECORDS_LIMIT) {
                                const logDistance = speedData.reduce((distance: number, logData, i: number) => {
                                    let endLog = i === speedData.length - 1 ? logData : speedData[i + 1];
                                    let startSpeed = logData.speed * 1000 / (60 * 60 * 1000); // speed in m/ms
                                    let finishSpeed = endLog.speed * 1000 / (60 * 60 * 1000); // speed in m/ms
                                    let startDate = new Date(logData.dateTime).getTime();
                                    let finishDate = new Date(endLog.dateTime).getTime();
                                    let avgSpeed = (finishSpeed + startSpeed) / 2;

                                    return distance + avgSpeed * (finishDate - startDate);
                                }, 0);

                                if (isDistanceProbable(logDistance, toDateTime(log.dateTime), toDateTime(nextLogDateTime))) {
                                    this.setDistanceAndOriginalDistanceOnLog(log, logDistance, logWithImprobableDistanceCache[log.id].originalDistance);
                                    delete logWithImprobableDistanceCache[log.id];
                                } else {
                                    // If distance still not correct, just use the original value
                                    setDistanceToOriginalValue(log);
                                }
                            } else {
                                // If distance can't be calculated, just use the original value
                                setDistanceToOriginalValue(log);
                            }
                        });
                        resolve();
                    })
                    .catch(reject);
            } else {
                resolve();
            }
        });
    }

    private logSuspiciousDistancesThatWereNotRecalculated(logWithImprobableDistanceCache: IIdDictionary<ITempDistanceCache>) {
        let suspiciousLogsSent: ISuspiciousLogsSent = extend(true, {}, this.store.getState().suspiciousLogsSent as ISuspiciousLogsSent);

        // Clear sended suspicious logs if app version changed
        if (!suspiciousLogsSent.version || suspiciousLogsSent.version !== this.myGeotabVersion) {
            suspiciousLogsSent = {
                version: this.myGeotabVersion,
                data: []
            };
        }

        const apiUser = this.api.getUser();
        Object.keys(logWithImprobableDistanceCache).forEach(logId => {
            if (suspiciousLogsSent.data.indexOf(logId) === -1) {
                logger.event(logger.Event.SUSPICIOUSLOGDISTANCE, logWithImprobableDistanceCache[logId].suspiciousDistanceString, apiUser);
                suspiciousLogsSent.data.push(logId);
            }
        });
        this.store.dispatch(SuspiciousLogsSentActions.setSuspiciousLogsSent(suspiciousLogsSent));
    }
}
